import { useEffect, useRef } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useCentralizedProviderData } from '@crehana/auth';
import { PageLoader } from '@crehana/compass.ui/v2';
import { HanaBannerCategories } from '@crehana/hana-ai/HanaBannerCategories';
import { useTranslation } from '@crehana/i18n';
import { useRBAC } from '@crehana/rbac/hooks/use-rbac';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Col, Container, Row } from '@crehana/ui';
import { useMe } from '@crehana/web';
import { useRouter } from 'next/router';

import { Header } from '@/views/AdminDashboard/components';
import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';

import { Card, Heading } from './components';
import {
  climateEngagementRecognitionModule,
  climateEngagementSurveyModule,
  learningCoursesModule,
  learningGroupStatisticsModule,
  learningSkillEvolutionModule,
  performanceCompetencesModule,
  performanceFeedbackModule,
  performancePidModule,
} from './constants';

const isProduction = process.env.APP_ENV === 'production';
const subDomain = isProduction ? 'crehana' : 'pruebas';

const Home: React.FC<React.PropsWithChildren<unknown>> = () => {
  const router = useRouter();
  const { t } = useTranslation();

  const { logEvent } = useAmplitude();
  const { hasProducts } = useRBAC();

  const { me: centralizedMe, loading: loadingCentralized } =
    useCentralizedProviderData();
  const { loading: loadingLearningMe } = useMe();
  const isCentralized = Boolean(centralizedMe);

  const {
    slug,
    isLoading: loadingAllowedModules,
    hasAdminModules,
  } = useAdminDashboard();

  const onChatCreation = (chatId: string) => {
    logEvent({
      event: 'Main Banner Crehana AI Clicked',
      data: {
        ...getSourceEventParams(),
        Organization: slug,
        Success: true,
      },
    });
    router.push(`/org/${slug}/ai/admin/chat/${chatId}`);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    logEvent({
      event: 'Render Panel Home Cross',
      data: {
        ...getSourceEventParams(),
        Organization: slug,
        Success: true,
      },
    });
  }, []);

  const hasRedirected = useRef(false);
  useEffect(() => {
    if (loadingAllowedModules || hasRedirected.current || !slug) {
      return;
    }

    if (!hasAdminModules) {
      router.replace(`/org/${slug}/panel/estadisticas/generales/`);
      hasRedirected.current = true;
    }
  }, [loadingAllowedModules, hasAdminModules, slug, router]);

  const getModules = () => {
    if (loadingCentralized || loadingLearningMe) {
      return [];
    }

    const modules = [];

    // Si NO hay centralizedMe, la cuenta NO está centralizada
    // Mostrar TODOS los módulos de productos
    if (!isCentralized) {
      modules.push(climateEngagementSurveyModule);
      modules.push(climateEngagementRecognitionModule);
      modules.push(learningSkillEvolutionModule);
      modules.push(learningGroupStatisticsModule);
      modules.push(learningCoursesModule);
      modules.push(performanceCompetencesModule);
      modules.push(performanceFeedbackModule);
      modules.push(performancePidModule);
      return modules;
    }

    if (hasProducts(['TALENT_CLIMATE'], { operator: 'AND' })) {
      modules.push(climateEngagementSurveyModule);
    }

    if (hasProducts(['TALENT_RECOGNITION'], { operator: 'AND' })) {
      modules.push(climateEngagementRecognitionModule);
    }

    if (hasProducts(['LEARNING'], { operator: 'AND' })) {
      modules.push(learningSkillEvolutionModule);
      modules.push(learningGroupStatisticsModule);
      modules.push(learningCoursesModule);
    }

    if (hasProducts(['TALENT_PERFORMANCE'], { operator: 'AND' })) {
      modules.push(performanceCompetencesModule);
      modules.push(performanceFeedbackModule);
      modules.push(performancePidModule);
    }

    return modules;
  };

  if (loadingAllowedModules || loadingCentralized || loadingLearningMe) {
    return <PageLoader />;
  }

  // We show the PageLoader if the user doesn't have admin modules because it's being redirected internally
  if (!hasAdminModules) {
    return <PageLoader />;
  }

  return (
    <>
      <div className="tw-pt-4 tw-pb-2.4 md:tw-pb-3.2">
        <Header customTitle={<Heading transparent />} transparent />
      </div>
      <section className="w-full mb-32">
        <Container type="dashboard" className="cui-dashboard-container">
          <Row gutter={16} className="tw-flex tw-justify-center">
            {isCentralized && (
              <Col style={{ maxWidth: '1070px' }}>
                <HanaBannerCategories
                  isFullWidth
                  onChatCreation={onChatCreation}
                  targetSegment="admin"
                />
              </Col>
            )}
            {getModules().map(currentModule => (
              <Col
                xs={12}
                sm={6}
                key={`card-${currentModule.url}`}
                style={{ maxWidth: '535px' }}
              >
                <Card
                  label={t(`${currentModule.translationKey}_LABEL`)}
                  title={t(`${currentModule.translationKey}_TITLE`)}
                  description={t(`${currentModule.translationKey}_DESCRIPTION`)}
                  cta={t(`${currentModule.translationKey}_CTA`)}
                  subdomain={subDomain}
                  {...currentModule}
                />
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Home;
